// Size expressions for the incident layers.
//
// Its own module, importing nothing, for the same reason pin-layout.ts is:
// map.ts pulls in maplibre-gl and a stylesheet, neither of which survives a
// test runner, and these are the numbers most worth testing. A style
// expression fails silently — MapLibre rejects the layer at load and draws
// nothing, with no error at the call site — so they are checked against the
// style spec rather than assumed.

/**
 * Incident circle radius, by zoom and by `scale` — customers off supply, for
 * power cuts.
 *
 * The counts are chosen to read at a glance rather than to be proportional: a
 * street and a town should look different, but a town and a city need not
 * differ by the ratio of their populations, or one dot would swallow the map.
 * A feature sending no scale evaluates the 0 stop, which is the radius every
 * layer had before any of this — so nothing but power moves.
 */
export const CIRCLE_STOPS = [
  { zoom: 8, radii: [[0, 5], [100, 9], [1000, 13]] },
  { zoom: 12, radii: [[0, 10], [100, 17], [1000, 25]] },
  { zoom: 16, radii: [[0, 18], [100, 30], [1000, 44]] },
] as const;

/**
 * The sea-station sprite curve.
 *
 * Large enough that a rig and a buoy are distinguishable as shapes, which is
 * the entire reason they are sprites rather than dots. The first attempt was
 * half this and read as two kinds of speck. Still below a vehicle: these are
 * the sea a helicopter is flying over, not the helicopter.
 */
export const STATION_SIZE_STOPS = [[5, 0.42], [9, 0.6], [14, 0.8]] as const;

/**
 * The tram-platform sprite curve.
 *
 * Its own curve rather than the station one, because tram platforms are the
 * only sprites on this map arranged in a **line**. Edinburgh's 44 sit along
 * 18.5 km of route from the airport to Newhaven, and the route is what a reader
 * wants from a height — so the mark has to be small enough up there that
 * consecutive stops stay separate beads rather than one purple smear.
 *
 * The numbers come off that spacing. At the opening zoom of 10.5 there are
 * roughly 60 m to the pixel at this latitude, which puts neighbouring platforms
 * about 14 px apart; the station curve would draw each one 20 px wide and close
 * the gaps entirely, so this one is at 0.22 there — about 10 px, half the pitch.
 *
 * Above that it climbs steeply to sit level with the sea-station curve by zoom
 * 14 and go past it by 17. The first version topped out well below the station
 * curve, which put a 29 px tram beside a 38 px taxi rank on the same street —
 * and the tram reads smaller still than the arithmetic says, because its
 * silhouette is mostly daylight where a black cab is a solid mass. Constrained
 * only at the bottom, then: the smear is a real limit, and there is no reason to
 * carry that restraint up to the zooms where a reader is looking at the shape.
 *
 * Nothing competes for the space at the low end: bus stops are not drawn at all
 * above STOP_MIN_ZOOM's scale, so at 10.5 these are the only stops on the map.
 *
 * Paired platforms are the one case this cannot fix. A tram stop is two NaPTAN
 * entities 20–40 m apart, so the pair overlaps below about zoom 16 whatever the
 * curve says — the same overlap the two directions of a journey time have, and
 * accepted for the same reason: they are one place, and they separate exactly
 * when a reader is close enough to want to tell them apart.
 */
export const TRAM_SIZE_STOPS = [[9, 0.16], [11, 0.24], [14, 0.78], [17, 0.95]] as const;

/**
 * icon-size for the tram-platform layer.
 *
 * A plain zoom interpolation rather than a branch of iconSizeExpression(),
 * because that expression exists to reconcile a sprite with the circle
 * underneath it and these have no circle — they have their own source, their own
 * layer and one sprite, so there is nothing to case on. Here rather than inline
 * in map.ts so it compiles against the real style spec in the script tests: a
 * malformed expression is rejected at layer load with nothing logged, and the
 * symptom would be the whole layer silently drawing nothing.
 */
export function tramSizeExpression(): unknown[] {
  return ['interpolate', ['linear'], ['zoom'], ...TRAM_SIZE_STOPS.flat()];
}

/**
 * The Subway-station sprite curve.
 *
 * The tram's numbers, and the arithmetic says they must be: fifteen stations
 * on a 10.4 km ring is roughly 700 m of pitch, which at the opening zoom's
 * ~60 m to the pixel is under 12 px between neighbours — *tighter* per mark
 * than the tram chain, whose 44 platforms are 22 stops when one direction is
 * counted. Declared separately rather than aliased, because the two networks
 * have no reason to move together — but any temptation to start this curve
 * larger loses to that 12 px, and the disc needs no size help anyway: it is a
 * solid mass where the tram silhouette is mostly daylight.
 */
export const SUBWAY_SIZE_STOPS = [[9, 0.16], [11, 0.24], [14, 0.78], [17, 0.95]] as const;

export function subwaySizeExpression(): unknown[] {
  return ['interpolate', ['linear'], ['zoom'], ...SUBWAY_SIZE_STOPS.flat()];
}

/**
 * The halves of the baked stop source.
 *
 * One source carries every kind and the layers split it, because they draw
 * differently in kind and not merely in colour: a bus stop is a circle that
 * fades in at walking scale; a tram platform and a Subway station are sprites
 * visible from the opening view. The split has to be a filter rather than
 * separate sources — the payload arrives as one fetch shared by all the
 * toggles, and main.ts hands the map whichever kinds the reader has switched
 * on.
 *
 * A ferry-flagged stop is deliberately claimed by NO layer. The terminal
 * already has a presence on the map — the ⚓ Ferries pin, whose panel shows
 * the baked sailings next to the AIS vessel status — and a second mark on the
 * same quay was exactly what shipping the ferry layer produced (15 Aug 2026,
 * removed the same day). The bus filter still excludes the flag, so a ferry
 * stop that reaches the source can never fall back to being a grey circle.
 *
 * Compared against the number 1, never against `true`: MapLibre's comparisons
 * are typed and a boolean tested against a number silently never matches, which
 * is why applyStops() stamps each flag as `1 | 0`. Every clause is written here
 * so the kinds can never drift into overlapping or into leaving one undrawn.
 */
export function tramStopFilter(): unknown[] {
  return ['==', ['get', 'tram'], 1];
}

export function subwayStopFilter(): unknown[] {
  return ['==', ['get', 'subway'], 1];
}

export function busStopFilter(): unknown[] {
  return ['all',
    ['!=', ['get', 'tram'], 1],
    ['!=', ['get', 'subway'], 1],
    ['!=', ['get', 'ferry'], 1],
  ];
}

/**
 * Divisor turning a circle radius into an icon-size for the bolt inside it.
 *
 * Lands the drawn bolt at roughly 60% of the disc's width. Larger than the
 * arithmetic suggests because the bolt occupies about 70% of its own sprite
 * box rather than all of it, and sizing against the box leaves it undersized.
 */
export const GLYPH_PER_RADIUS = 32;

/**
 * Sprites drawn *instead of* a circle.
 *
 * Keyed on the sprite rather than on the provider, which is the fix for a bug
 * this list exists because of: the circle layer excluded `providerId ==
 * 'roadworks'`, and when planned works arrived as `roadworks-future` they got
 * a circle underneath their cone. A provider id is a deployment detail; which
 * sprite a feature draws is the actual question, and a new layer that picks an
 * existing sprite now inherits the right behaviour without touching the map.
 */
import type { StyleSpecification } from 'maplibre-gl';

import { airIconNames } from './air-colours.ts';
import { festivalIconNames } from './festival-colours.ts';

export const REPLACES_CIRCLE = [
  'fuel', 'rig', 'buoy',
  // A hackney seen head on is a shape worth recognising, like the rig, and a
  // rank has no severity for a disc to carry — so the cab stands on the map
  // by itself, white halo against the basemap.
  'taxi',
  // The festival stars, for the same reasons as the cab — a venue has no
  // severity, and three hundred of them need the cheapest possible read. One
  // name per festival, spread from the colour table so a new festival cannot
  // be drawn on a disc by having been forgotten here.
  ...festivalIconNames(),
  // A camera is an object with a shape worth recognising, like the cab and
  // the rig, and carries no severity for a disc to encode. Drawn by its own
  // layer, not the shared sprite layer — see cameraLayerFilter().
  'camera',
  'roadworks', 'roadworks-future', 'incident', 'incident-bridge', 'road-closed', 'vms',
  // A stopwatch is a shape worth recognising and carries no severity, so it
  // replaces its dot rather than sitting on one. Missing from this list, every
  // journey time drew as a plain blue disc with the sprite lost on top of it —
  // the same bug planned roadworks had, which is why the list is keyed on the
  // sprite and not the provider.
  'journey', 'journey-slow', 'journey-stopped',
  // The air quality pins draw their own body in the index's colour, so a
  // severity disc behind one would be a second, differently-coloured mark
  // saying the same thing in a coarser vocabulary. Spread from the ramp so
  // that adding a value cannot leave one off this list — the exact bug the
  // journey stopwatch had.
  ...airIconNames(),
] as const;


/**
 * Where a sprite sits relative to the point it marks.
 *
 * Everything here is centred except the air quality pins, which are teardrops
 * with a point at the bottom: centre them and the tip hangs below the station
 * by half the pin, marking a spot where nothing is. `bottom` puts the tip on
 * the coordinate, which is what a pin is for.
 *
 * Data-driven rather than a second symbol layer, because a second layer would
 * need its own copy of the filter, the sizing and the sort key — three things
 * to keep in step for one property.
 */
export function iconAnchorExpression(): unknown[] {
  return ['case',
    ['in', ['get', 'icon'], ['literal', airIconNames()]], 'bottom',
    'center'];
}

/**
 * Which features the incident circle layer draws a dot for.
 *
 * Here rather than inline in map.ts so it can be compiled against the real
 * style spec and evaluated — see scripts/test-map-expressions.mjs. A filter
 * fails the same silent way an expression does, and the failure this one
 * exists to prevent is not a crash but a stray blue disc behind a cone, which
 * only a person looking at the map would ever notice.
 *
 * The two provider exclusions stay provider-keyed: fuel and sea-station
 * features carry no `icon` of their own, so there is nothing else to match on.
 */
export function incidentCircleFilter(): unknown[] {
  return ['all',
    ['!=', ['get', 'providerId'], 'fuel'],
    ['!=', ['get', 'providerId'], 'waves'],
    ['!', ['in', ['get', 'icon'], ['literal', [...REPLACES_CIRCLE]]]],
    // A feature drawn as its own shape draws no point marker — see isQuiet().
    ['!=', ['get', 'quiet'], 1],
  ];
}

/**
 * Which features the sprite layer draws a glyph for.
 *
 * Anything carrying a sprite, less the quiet ones. Lifted out of map.ts to sit
 * beside the circle filter for one reason: the two now share a clause, and a
 * clause that exists in two files is a clause that will be changed in one.
 *
 * Cameras are excluded because they have a layer of their own — see
 * cameraLayerFilter() for why — and a sprite drawn by two layers is two
 * popups stacked on one click, the bug the REPLACES_CIRCLE machinery exists
 * to prevent arriving by a new door.
 */
export function spriteLayerFilter(): unknown[] {
  return ['all',
    ['!=', ['get', 'icon'], ''],
    ['!=', ['get', 'icon'], 'camera'],
    ['!=', ['get', 'quiet'], 1],
  ];
}

/**
 * Which features the camera layer draws.
 *
 * Cameras get a symbol layer of their own rather than riding the shared
 * sprite layer, for two properties that layer cannot give them per feature:
 *
 *  - **Decluttering.** The shared layer sets icon-allow-overlap because rigs
 *    and power bolts must never flicker out. Cameras are the opposite case —
 *    Traffic Scotland mount them in clusters on dual carriageways, and a
 *    stack of identical grey housings reads as one camera with rendering
 *    artefacts. Overlap is a per-layer layout property, so a per-layer split
 *    is the only way the two rules can coexist.
 *  - **The stale fade.** icon-opacity is paint on the whole layer's
 *    expression; the camera one reads the `stale` flag, which no other sprite
 *    carries — see cameraOpacityExpression().
 *
 * The quiet clause is inherited from the shared filter so the popup-ownership
 * rule stays one rule: a quiet feature belongs to its line, whatever sprite
 * it carries.
 */
export function cameraLayerFilter(): unknown[] {
  return ['all',
    ['==', ['get', 'icon'], 'camera'],
    ['!=', ['get', 'quiet'], 1],
  ];
}

/**
 * Sprites whose layer paints the road itself, so a calm one needs no marker.
 *
 * This list is the *scope* of isQuiet(), and it exists because leaving it out
 * broke five layers at once. The rule started as "quiet when it has a line and
 * is not delayed", which was implicitly scoped — only journey times ever had a
 * line. Dropping the line condition to stop green stopwatches cluttering the
 * map dropped the scoping with it, and suddenly every calm feature on the map
 * qualified: river gauges, tide stations, taxi ranks, wave buoys and fuel
 * forecourts all report `info` or `ok`, all went quiet, and all disappeared.
 *
 * Keyed on the sprite rather than the provider id, like REPLACES_CIRCLE and
 * for the same reason — a second layer that draws coloured roads should
 * inherit this by picking the sprite, not by being named here.
 */
/**
 * The colour a warning's own outline is drawn in — the publisher's, not ours.
 *
 * A Met Office warning is the one feature on this map whose colour the reader
 * already knows before they arrive. "Yellow warning" is the name of the thing;
 * it is on the forecast, in the headline and on the radio, and a yellow
 * warning drawn in the zone green said the map disagreed with the news.
 *
 * Green is right for a Low Emission Zone and wrong here, and the reason is in
 * ZONE_GREEN's own comment: a zone is a *category* — there is nothing good or
 * bad about being inside one — and a weather warning is a *state*, with a
 * level the publisher grades it by. Sharing one paint made the second look
 * like the first.
 *
 * `severity` cannot do this job. It has five values for the whole site and
 * puts AMBER and RED in the same bucket, which is correct for sorting a Now
 * box and useless for a colour a reader recognises. Hence LiveFeature.level.
 *
 * The hues are the Met Office's published key, not an interpretation of it.
 * A first pass darkened them for legibility on a pale basemap — their yellow
 * is very nearly white against grey streets — which is a reasonable instinct
 * and the wrong call here: the colour is not decoration on the warning, it is
 * the warning's name, and a yellow warning drawn in gold is a third colour
 * nobody published. Legibility is bought with the dark casing under the
 * outline instead, the way the journey-time line buys it.
 */
export const WARNING_COLOUR: Record<string, string> = {
  yellow: '#ffdd00',
  amber: '#ff9500',
  red: '#d0103a',
};

/**
 * How much of the map a filled area is allowed to take, and why a warning
 * takes more of it than a zone.
 *
 * Not a preference. The first shipped attempt used one weight for both and the
 * yellow came out tan — reported as "this looks orange to me", and it was.
 * A hatch at 0.55 leaves nearly half the basemap showing through every stroke,
 * and mixing a very light, very saturated colour with a warm grey shifts it
 * toward khaki. Green never showed the fault because it sits mid-luminance and
 * mixes toward grey rather than toward brown, which is exactly the kind of
 * thing one worked example hides.
 *
 * The second attempt raised the hatch's opacity and it was still cream, which
 * is the part worth understanding rather than tuning around. **Opacity is not
 * coverage.** A hatch of 1.6 px strokes on an 8 px tile paints about a third
 * of its pixels; the rest is basemap, whatever the opacity says. And yellow is
 * already a very light colour, so mixing it with a light warm grey cannot
 * darken it — it can only desaturate it, toward cream, which reads as sand.
 * There is no opacity at which a sparse yellow hatch looks yellow.
 *
 * So the warning's strokes are nearly twice as wide, which is what actually
 * changes how much of the area is yellow, and the wash under them comes back
 * up to carry the gaps. Measured by rendering four candidates beside the
 * published colour bar and looking, rather than reasoning about numbers.
 *
 * The zone weights are unchanged, deliberately. A Low Emission Zone sits over
 * a city centre permanently and the argument in ZONE_GREEN's comment still
 * holds for it: a fill strong enough to notice flattens every street under it.
 * A warning covers a region, lasts a day, and is the reason somebody opened
 * the map — different claims on the reader's attention, and now different
 * numbers rather than one compromise.
 */
export const ZONE_PAINT = {
  /** A designated area: context, drawn lightly. */
  zone: { fill: 0.10, hatch: 0.55, stroke: 1.6 },
  /** A warning: the thing itself, drawn in the colour it is named after. */
  warning: { fill: 0.18, hatch: 0.95, stroke: 3.0 },
} as const;

/**
 * That colour for a feature's published level, or nothing.
 *
 * Nothing, rather than a default, is the load-bearing half: a zone with no
 * level is a Low Emission Zone and must stay green, and an unrecognised level
 * is a word this palette has never seen — colouring it yellow would be a
 * confident guess about a warning we cannot read. The caller falls back to
 * ZONE_GREEN, which says "an area" and claims nothing about how bad it is.
 */
export function zoneColour(level: string | undefined): string | undefined {
  return WARNING_COLOUR[String(level ?? '').trim().toLowerCase()];
}

/**
 * The card's own ground, from --panel in style.css.
 *
 * A constant rather than a computed style: whether a colour can be read on the
 * card has to be decidable before anything is rendered.
 */
export const CARD_BG = '#0f1720';

/** WCAG relative luminance of a #rrggbb colour. */
export function luminance(hex: string): number {
  const channel = (i: number) => {
    const c = parseInt(hex.slice(1 + i * 2, 3 + i * 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(0) + 0.7152 * channel(1) + 0.0722 * channel(2);
}

/** Contrast ratio between two #rrggbb colours, 1 to 21. */
export function contrast(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi! + 0.05) / (lo! + 0.05);
}

/**
 * The colour a card is accented with: the publisher's, where they grade it.
 *
 * A card headed "YELLOW WARNING IN FORCE" in amber is the map's own fault
 * repeated in words. `severity` is our five-value scale and its `minor` is
 * amber — a different published level — so where a feature carries the
 * publisher's own level, that is the colour it is known by.
 */
export function accentColourFor(level: string | undefined, fallback: string): string {
  return zoneColour(level) ?? fallback;
}

/**
 * The same colour for the status line, unless it cannot be read there.
 *
 * The accent is a bar and only has to be seen; the status is words and has to
 * be read, on a near-black card. Yellow and amber clear AA on it comfortably;
 * the Met Office's red is 3.3:1 and does not, so it falls back to the severity
 * palette's brighter red — which is what a reader sees for anything else major
 * and is legible.
 *
 * A threshold rather than a special case for red, so a colour added later is
 * judged rather than assumed.
 */
export function statusColourFor(level: string | undefined, fallback: string): string {
  const own = accentColourFor(level, fallback);
  return contrast(own, CARD_BG) >= 4.5 ? own : fallback;
}

export const COLOURS_ITS_OWN_ROAD = ['journey', 'journey-slow', 'journey-stopped'] as const;

/**
 * Whether a feature is too calm to be worth a marker.
 *
 * The rule Traffic Scotland's own congestion map follows: colour the road, and
 * put a marker on it only where the colour is something other than "moving".
 * A green stopwatch is the same fact the green road already states, and
 * stating it fifteen hundred times is the difference between a map you can
 * read and a wall of icons — which is what this layer looked like before, and
 * why it shipped switched off by default.
 *
 * **Green never draws a marker, line or no line.** The first version made it
 * conditional on having a road path, on the reasoning that a section which
 * failed to route would otherwise vanish. On the map that reasoning turned
 * inside out: the sections that fail to route are not scattered, they cluster
 * — around Kirkliston and Newbridge and the Queensferry approaches — so the
 * exception put its densest wall of green stopwatches exactly where the
 * colouring was already weakest. A gap in the colour is honest about being a
 * gap. Forty green icons over the same gap is just clutter that also happens
 * to hide it.
 *
 * What that costs, stated plainly: a green section with no line is not drawn
 * at all and cannot be clicked. That is the intended trade — the layer answers
 * "what is slow", and a normal journey on a road with no colouring is the
 * least interesting thing on the map.
 *
 * A delay always keeps its marker, because "click here for which direction and
 * how long" is the whole point of the ones that are not green.
 *
 * **Only sprites in COLOURS_ITS_OWN_ROAD can go quiet.** A river gauge is calm
 * almost every day of its life and there is nothing else drawing it — going
 * quiet would simply delete the layer. Suppressing a marker is only honest
 * where something else is already saying the same thing.
 */
export function isQuiet(icon: string, severity: string): boolean {
  if (!COLOURS_ITS_OWN_ROAD.includes(icon as typeof COLOURS_ITS_OWN_ROAD[number])) return false;
  return severity !== 'minor' && severity !== 'major';
}

/**
 * Sprites drawn *inside* their circle rather than instead of it.
 *
 * The distinction is what the circle is still carrying. A power cut needs its
 * colour for severity and its size for how many customers, so the glyph goes
 * on top and says which of the coloured dots is which. A rig replaces its
 * circle outright, because a rig is a shape worth recognising and there is no
 * severity to encode.
 *
 * River gauges join the first group: the dot is the layer's blue and the glyph
 * says gauge, with an arrow for which way the water is going.
 */
export const INSIDE_CIRCLE = [
  'power',
  'river', 'river-rising', 'river-falling', 'river-steady',
  'tide-rising', 'tide-falling', 'tide-flat',
] as const;

/**
 * Smallest circle that can legibly hold a glyph, by zoom.
 *
 * A dot carrying a sprite has to be big enough to read it, and the plain
 * incident radius is not: at zoom 12 an info-severity dot is 10px across,
 * which leaves about 6px of glyph — a smudge. Power cuts grow with the number
 * of customers and get there on their own; a river gauge has no such number,
 * so it needs a floor.
 *
 * Applied as a floor rather than a replacement, so a power cut affecting a
 * thousand people keeps the size it earned.
 */
export const GLYPH_MIN_RADIUS = [[8, 7], [12, 13], [16, 22]] as const;

/** Fraction of its sprite box the drawn bolt actually occupies. */
export const BOLT_BOX_FILL = 0.7;

/** Logical width of a sprite at icon-size 1 — a 96px canvas at pixelRatio 2. */
export const SPRITE_BASE_PX = 48;

/**
 * The camera sprite curve, in drawn pixels over the sprite base.
 *
 * The design handoff's three sizes verbatim: 22px zoomed out, 30px at city
 * zoom — the same 30px the compact map pins use, which is what clears the
 * 24px accessible-target minimum — and 44px at walking zoom. Its own curve
 * rather than the sea-station one because these sit on streets among other
 * road furniture, not alone offshore, and the station curve's 38px at zoom 14
 * crowds a carriageway that also carries cones and stopwatches.
 */
export const CAMERA_SIZE_STOPS = [
  [10, 22 / SPRITE_BASE_PX],
  [13, 30 / SPRITE_BASE_PX],
  [16, 44 / SPRITE_BASE_PX],
] as const;

export function cameraSizeExpression(): unknown[] {
  return ['interpolate', ['linear'], ['zoom'], ...CAMERA_SIZE_STOPS.flat()];
}

/**
 * Fading is the whole staleness signal — no badge, no ribbon, no age row; the
 * user reviewed and rejected restating a time the image already shows. The
 * flag is stamped 1/0 by the GeoJSON mapping (see applyIncidents), compared
 * as a number for the reason every flag on this map is, and read here rather
 * than thresholded here: main.ts owns the 20-minute rule so the sprite, the
 * popup image and the control-room tiles cannot disagree about what stale is.
 */
export function cameraOpacityExpression(): unknown[] {
  return ['case', ['==', ['get', 'stale'], 1], 0.5, 1];
}

/**
 * The count plate on a cluster cone, in the sprite's own 96 px canvas.
 *
 * Here rather than in icons.ts because both halves need it: icons.ts draws the
 * plate and the symbol layer has to land the number on it. A plate drawn in
 * one file and a text offset guessed in another is the two-halves failure
 * CLAUDE.md's trap 8 describes — each half right, the join wrong, and the
 * symptom a number sitting beside its own plate with nothing failing anywhere.
 *
 * The mark is shrunk and lifted rather than redrawn, so a cluster carries the
 * same silhouette one of its members does. That is the whole point: a reader
 * who has learned "cone means roadworks" reads a cluster of them without being
 * told, which a coloured disc with a number on it cannot claim — and the
 * sewer overflows get it for nothing, because on that layer the disc's colour
 * already is the state. It stays true when defibrillators and lifebelts
 * (#8, #9) want the same treatment with their own sprites.
 */
export const COUNT_PLATE = {
  markScale: 0.8,
  markShift: -12,
  /*
   * Wide enough for three digits at every size the cone is drawn at, which is
   * what lets the number be centred with one constant offset instead of a
   * case per digit count — shrinking the type for a wider number would move it
   * in ems and walk it off its own plate. Glasgow held 235 works on the first
   * real bake, so three digits is a real count and not a defensive guess.
   *
   * Narrow enough not to be the widest thing on the mark. The first version
   * was 66 wide, against a cone base of 43 and an overflow disc of 51, so
   * every cluster read as a white bar with something small perched on it —
   * and two states clustered in one place read as two overlapping bars rather
   * than as a green count and an amber one. At 58 the mark is its own shape
   * again, with the plate a base under it. Half of 58, plus the 4 px stroke,
   * is 33 — inside the 96 px canvas.
   */
  width: 58,
  height: 22,
  top: 12,
  radius: 9,
} as const;

/**
 * The count's type size, as a multiple of the sprite's.
 *
 * Tied to the icon rather than to zoom because `text-offset` is measured in
 * ems: a type size that did not track the sprite would slide the number off
 * the plate as the cone grew. One ratio means the mark scales as a drawing.
 */
export const CLUSTER_TEXT_PER_ICON = 10;

/** Where the count sits, in ems below the sprite's centre — plate geometry, not a guess. */
export function clusterTextOffsetEm(): number {
  const centrePx = (COUNT_PLATE.top + COUNT_PLATE.height / 2) / SPRITE_PIXEL_RATIO;
  return centrePx / CLUSTER_TEXT_PER_ICON;
}

/**
 * How big the cone is, by how many works it stands for.
 *
 * Not proportional, for the reason CIRCLE_STOPS gives about power cuts: three
 * works and thirty should look different, but thirty and three hundred need
 * not differ by ten times or one mark would swallow the city. Comfortably
 * above a single cone, which runs 0.42 to 0.8 across the zooms it is drawn at
 * — see STATION_SIZE_STOPS — so a count never reads as one more works.
 *
 * On the count alone and not on zoom: a cluster means the same thing at every
 * zoom it is drawn at, and zoom already decides whether it exists at all.
 */
export const CLUSTER_ICON_STOPS = [[3, 1.0], [10, 1.15], [50, 1.35], [250, 1.6]] as const;

export function clusterIconSizeExpression(): unknown[] {
  return ['interpolate', ['linear'], ['get', 'count'], ...CLUSTER_ICON_STOPS.flat()];
}

export function clusterTextSizeExpression(): unknown[] {
  return ['interpolate', ['linear'], ['get', 'count'],
    ...CLUSTER_ICON_STOPS.flatMap(([count, size]) => [count, size * CLUSTER_TEXT_PER_ICON])];
}

/**
 * The count's ink.
 *
 * A constant, because the plate under it is a constant: white, drawn into the
 * sprite. The first version painted the bubble in its worst member's severity
 * colour and picked black or white against it — which was a rule the cone
 * itself does not follow. coneRender's own comment in icons.ts settles it:
 * "tinting it red or blue to encode severity would fight a signal people
 * already read without thinking". A cluster of works had no severity to show
 * that its members were not already showing, so it shows none.
 */
export const COUNT_INK = '#0b1016';

type Stops = readonly (readonly [number, number])[];

/** Linear interpolation over a stop list, clamped at both ends as MapLibre does. */
export function atZoom(stops: Stops, zoom: number): number {
  let previous: readonly [number, number] | null = null;
  for (const stop of stops) {
    const [z, value] = stop;
    // Below the first stop MapLibre clamps rather than extrapolating, and so
    // must this: the two are compared against each other in the tests.
    if (previous === null) {
      if (zoom <= z) return value;
    } else if (zoom <= z) {
      const [previousZoom, previousValue] = previous;
      return previousValue
        + ((value - previousValue) * (zoom - previousZoom)) / (z - previousZoom);
    }
    previous = stop;
  }
  if (previous === null) throw new Error('atZoom needs at least one stop');
  return previous[1];
}

/**
 * The scale→radius stops at one zoom, interpolated between the defined zooms.
 *
 * Needed because the glyph expression has to be evaluated at zooms the radius
 * expression does not name, and restating the numbers there by hand is exactly
 * how the bolt and the disc would drift apart.
 */
export function radiusStopsAt(zoom: number): [number, number][] {
  return CIRCLE_STOPS[0].radii.map(([count], index) => {
    // Transposed: one curve per customer count, across the zooms. Every zoom
    // row has to list the same counts in the same order for that to mean
    // anything, so a row missing one is a mistake worth stopping on rather
    // than a gap to interpolate over.
    const curve = CIRCLE_STOPS.map((stop) => {
      const point = stop.radii[index];
      if (!point) throw new Error(`CIRCLE_STOPS zoom ${stop.zoom} has no radius for ${count}`);
      return [stop.zoom, point[1]] as const;
    });
    return [count, atZoom(curve, zoom)];
  });
}

/**
 * The feature's scale, or zero.
 *
 * coalesce as a second line of defence, the same one icon-rotate uses: the
 * GeoJSON mapping already writes `scale: 0` for every layer that has no such
 * number, but `['get']` on a missing property returns null and interpolate
 * evaluates to null rather than to a radius — a circle that does not draw. A
 * feature is not worth losing over a property nobody promised.
 */
const SCALE = ['coalesce', ['get', 'scale'], 0];

/** True when this feature draws a sprite on top of its circle. */
const CARRIES_GLYPH = ['in', ['get', 'icon'], ['literal', [...INSIDE_CIRCLE]]];

/**
 * Radius by zoom and by customers off supply, floored for glyph-bearing dots.
 *
 * The floor lives inside each stop output rather than wrapping the expression,
 * for the same reason the icon-size branch does: only one zoom-based
 * interpolate is allowed per expression, so anything varying by zoom has to be
 * resolved at the stops.
 */
export function circleRadiusExpression(): unknown[] {
  return ['interpolate', ['linear'], ['zoom'], ...CIRCLE_STOPS.flatMap((s) => {
    const byScale = ['interpolate', ['linear'], SCALE, ...radiusStopsAt(s.zoom).flat()];
    return [s.zoom, [
      'case',
      CARRIES_GLYPH,
      ['max', byScale, atZoom(GLYPH_MIN_RADIUS, s.zoom)],
      byScale,
    ]];
  })];
}

/**
 * icon-size for the sprite layer, which carries two kinds of sprite with
 * different jobs.
 *
 * A power bolt sits *inside* a circle, so it must be sized off that circle
 * rather than off the zoom alone. At a fixed size it overflowed the small dots
 * — a fault affecting nobody and one affecting a hundred people both came out
 * as a bolt with a ring round it, which cancels the size encoding the circle
 * exists to carry.
 *
 * A rig or a buoy replaces its circle, so it keeps the shape-legibility curve.
 *
 * The branch lives inside each stop output rather than wrapping the whole
 * thing, because a style may contain only one zoom-based interpolate per
 * expression — the readable version, a `case` with two zoom curves under it,
 * is rejected at style load.
 *
 * So the two curves have to share one set of zoom stops, and the set is the
 * union of both rather than either alone. Resampling the station curve onto
 * the circle's zooms quietly shrank rigs by 5% at zoom 14, which is the sort
 * of change nobody asks for and nobody notices: between consecutive stops of
 * the union each original curve is linear, so both come out exact.
 */
export function iconSizeExpression(): unknown[] {
  const zooms = [...new Set([
    ...CIRCLE_STOPS.map((s) => s.zoom),
    ...STATION_SIZE_STOPS.map(([z]) => z),
  ])].sort((a, b) => a - b);

  return ['interpolate', ['linear'], ['zoom'], ...zooms.flatMap((z) => {
    const floor = atZoom(GLYPH_MIN_RADIUS, z);
    return [z, [
      'case',
      CARRIES_GLYPH,
      // Sized off the circle it sits in, floor included, so the two agree at
      // every zoom and every count without either restating the other.
      ['interpolate', ['linear'], SCALE,
        ...radiusStopsAt(z).flatMap(([scale, r]) =>
          [scale, Math.max(r, floor) / GLYPH_PER_RADIUS])],
      atZoom(STATION_SIZE_STOPS, z),
    ]];
  })];
}

// ── Route lines: which way does this service run? ───────────────────────────
//
// A drawn route was one yellow stripe with no direction in it, and both
// directions of a service were drawn on top of each other — so a loop, or a
// one-way section through a city centre, told a reader nothing about which way
// anything travels (issue #40).
//
// The geometry already knows. GTFS shapes.txt is published in travel order and
// the bake sorts by sequence before thinning, so **the coordinate order of a
// baked line is the direction of travel**, and each direction is baked as its
// own line. Nothing upstream had to change; this is the rendering catching up
// with what the data has always said.

/**
 * Which side of the road a direction is drawn on, in pixels.
 *
 * `off` is +1, -1 or 0, set per feature when the route is handed to the map:
 * the two directions of a service are pushed apart so that each can carry its
 * own arrows, and a route with only one baked line stays exactly where it was.
 * Nudging a single-direction route off the street it runs along would be a
 * cosmetic lie about where the bus goes.
 *
 * The separation grows with zoom because it is a screen-space effect: at city
 * zoom two lines 5 px apart are a smear, and at street zoom 1.5 px apart is
 * one line.
 */
export function routeOffsetExpression(): unknown[] {
  // Zoom on the outside, the per-feature multiply under each stop. The obvious
  // spelling — one zoom curve multiplied by ['get', 'off'] — is rejected by the
  // style spec ("zoom expression may only be used as input to a top-level step
  // or interpolate"), and MapLibre's way of reporting that at runtime is to
  // draw nothing at all. Same shape as iconSizeExpression above, for the same
  // reason, and scripts/test-map-expressions.mjs is what caught it.
  return ['interpolate', ['linear'], ['zoom'],
    ...[[10, 1.2], [14, 2.6], [18, 5]].flatMap(([zoom, px]) =>
      [zoom, ['*', ['get', 'off'], px]]),
  ];
}

/**
 * Which side each baked direction is drawn on: +1, -1, or 0 for all of them.
 *
 * Zero when there is only one line, and that is the case worth stating: most
 * of the difference between "helpful" and "wrong" here is not drawing a route
 * off the street it runs along when there is no second direction to separate
 * it from.
 *
 * GTFS `direction_id` is 0 or 1, so two is the real maximum; anything beyond
 * the first still goes to the far side rather than being dropped, because a
 * line drawn on the wrong side is a smaller error than a line missing.
 */
export function routeOffsets(count: number): number[] {
  if (count < 2) return new Array(Math.max(0, count)).fill(0);
  return Array.from({ length: count }, (_, i) => (i === 0 ? 1 : -1));
}

/**
 * The sprite canvas every icon is drawn on, and the halo draw() strokes round
 * the shape. Both in canvas units; buildIcons registers at pixelRatio 2, so an
 * icon-size of 1 puts the whole canvas on screen at 48 CSS pixels.
 */
const SPRITE_CANVAS_PX = 96;
const SPRITE_PIXEL_RATIO = 2;
const SPRITE_HALO_PX = 7;

/**
 * The chevron itself, in canvas units from the centre. icons.ts draws exactly
 * these numbers and nothing else.
 *
 * They live here, next to the size curve, because the two are one decision and
 * were briefly two. The first version drew a chevron spanning ±15 in a canvas
 * where every other sprite uses ±34 — a third of the ink — and then chose
 * icon-size as though the shape filled it. The arrows came out around five
 * pixels on an eight-pixel line: present, correct, and unreadable.
 *
 * The test measured the canvas rather than the shape, so it agreed. Keeping the
 * geometry and the sizing in one file is what lets routeArrowHeightPx() below
 * be derived rather than guessed, and lets the test assert the thing a reader
 * actually sees.
 */
export const ROUTE_ARROW = { back: -40, tip: 42, flank: 30, notch: -22 };

/** How tall a drawn arrow is, across the line, in CSS pixels at `size`. */
export function routeArrowHeightPx(size: number): number {
  const ink = (ROUTE_ARROW.flank * 2 + SPRITE_HALO_PX) / SPRITE_CANVAS_PX;
  return (SPRITE_CANVAS_PX / SPRITE_PIXEL_RATIO) * size * ink;
}

/** And how long it is along the line — what has to fit inside the spacing. */
export function routeArrowLengthPx(size: number): number {
  const ink = (ROUTE_ARROW.tip - ROUTE_ARROW.back + SPRITE_HALO_PX) / SPRITE_CANVAS_PX;
  return (SPRITE_CANVAS_PX / SPRITE_PIXEL_RATIO) * size * ink;
}

/**
 * How big the direction arrows are.
 *
 * Sized against the line they sit on rather than against the sprite: the route
 * is 2 px wide at zoom 8, 4.5 at 14 and 8 at 18, and a dark chevron on a
 * yellow line has to be comfortably taller than the line or it reads as a
 * smudge in it. These put the arrow at roughly twice the line's width — about
 * 7, 11 and 17 CSS px — which is what routeArrowHeightPx() asserts.
 */
export function routeArrowSizeExpression(): unknown[] {
  return [
    'interpolate', ['linear'], ['zoom'],
    10, 0.20,
    14, 0.34,
    18, 0.50,
  ];
}

/**
 * How far apart the arrows sit, in pixels.
 *
 * Wide enough that the line is mostly line rather than mostly arrows, close
 * enough that a short stretch on screen still carries at least one. Constant
 * across zoom on purpose: this is a screen-space rhythm, and letting it scale
 * would put arrows on top of each other exactly when the line is most crowded.
 */
export const ROUTE_ARROW_SPACING_PX = 90;

/**
 * Where a feature sits in the stack when several share a point.
 *
 * **The rule: severity always wins.** More serious draws on top, without
 * exception, and size is only a tiebreak between things of equal severity.
 *
 * This came from Ardallie, where SSEN report two faults at identical
 * coordinates — a low-voltage one with nobody off, and a high-voltage one with
 * 31 customers off. Draw order was whatever the array happened to be, so the
 * small green circle landed on top of the large red one and the map showed a
 * restored fault while a live outage sat underneath it. A map that hides the
 * worse of two things is worse than useless: it is confidently reassuring.
 *
 * An earlier attempt at this sorted by size alone, putting big circles at the
 * bottom so small ones stayed visible. That reads well and is still wrong —
 * it happened to leave the green dot on top again. Visibility of everything is
 * a nice property; showing the most serious thing is a requirement.
 *
 * Within one severity, the larger circle goes underneath so the smaller stays
 * visible — that is where the size tiebreak earns its place.
 *
 * The compensation for covering something up is that a click surfaces every
 * feature under the cursor, not just the top one.
 */
/*
 * `ok` above `unknown`, and the order of those two matters as much as the
 * rest. Nigg WWTW Girdleness settled it (20 Aug 2026): Scottish Water put an
 * EO monitor and a CSO monitor on the same outfall, one reporting "no
 * overflows" and the other "no data — not monitored in real time", and the
 * grey drew over the green. `ok` is a claim of fine made by a working feed;
 * `unknown` is the absence of a claim; when they share a point the claim is
 * the information. Every per-feature `unknown` on this map means "no
 * reading" — an unpriced forecourt, a stale festival sync, an unmonitored
 * overflow — while feed-level unknowns (power data unavailable) are status
 * rows, never stacked markers, so nothing needed the old order.
 */
const STACK_RANK: Record<string, number> = {
  major: 5, minor: 4, info: 3, ok: 2, unknown: 1,
};

export function stackOrder(severity: string, scale: number): number {
  // A million apart, so no plausible customer count can promote one band into
  // another: a thousand-customer planned outage still sits below a one-customer
  // live one.
  return (STACK_RANK[severity] ?? 0) * 1_000_000 - Math.min(scale, 999_999);
}


// ── The basemap of last resort ──────────────────────────────────────────────

/**
 * Raster OSM, defined inline so it depends on no style host.
 *
 * Development only, and `fallbackStyle` below is what makes that true rather
 * than merely written down. It lives in this module, with the expressions,
 * because that is the half of the map a test runner can import: map.ts pulls
 * in maplibre-gl and a stylesheet, and a rule that can only be reached by
 * loading those is a rule nothing checks.
 */
const RASTER_FALLBACK: StyleSpecification = {
  version: 8,
  sources: {
    osm: {
      type: 'raster',
      tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
      tileSize: 256,
      attribution: '© OpenStreetMap contributors',
    },
  },
  layers: [{ id: 'osm', type: 'raster', source: 'osm' }],
};

/**
 * The style to fall back to when no vector style loads — or null, in a
 * production build, because there is not one.
 *
 * The OSM tile usage policy does not permit pointing a public site at
 * tile.openstreetmap.org, so `connect-src` does not name it and must not. That
 * made the production fallback a request the browser refused: a blank map, a
 * console full of violations, and the UI told nothing for another eight
 * seconds, since setStyle does not throw when it is the tiles that get
 * blocked. Answering null instead lets the caller say the basemap is
 * unavailable, which it already knows how to do.
 *
 * Takes `dev` rather than reading `import.meta.env` so it can be asked both
 * questions from a test. The two answers are checked against the deployed
 * policy in scripts/test-csp.mjs — if the host is ever allowed there, or the
 * gate is ever removed, exactly one of those assertions fails.
 */
export function fallbackStyle(dev: boolean): StyleSpecification | null {
  return dev ? RASTER_FALLBACK : null;
}
